import { AREA_CLS, Collapsible, type CollapsibleTone } from "@/shared/ui/Collapsible";
import { formatMoney } from "@/shared/format";
import type { OrderMarginRow } from "../../economicSummaryApi";
import type { Order } from "../../types";
import { LABEL, PANEL, TITLE } from "./stile";

function euroStr(v: string | null | undefined, currency?: string): string {
  const n = v == null ? null : Number(v);
  return n == null || Number.isNaN(n) ? "n/d" : formatMoney(n, currency);
}

// margin_percent è `string | null` (Number(null) === 0, non NaN): senza guard
// una riga computable con percentuale nulla stamperebbe «0%» — un numero
// falso sulla rail conti. Stessa degradazione onesta di euroStr → «n/d».
function percentStr(v: string | null | undefined): string {
  const n = v == null ? null : Number(v);
  return n == null || Number.isNaN(n) ? "n/d" : `${n.toLocaleString("it-IT")}%`;
}

interface ContiRow {
  k: string;
  v: string;
  strong?: boolean;
  cls?: string;
}

/** Voce in detrazione: «− € 12,00» in rosso. Quando il valore manca, il segno
 *  mentirebbe («− n/d» si legge come una detrazione avvenuta di importo ignoto)
 *  e il rosso pure: la voce degrada a «n/d» neutro. */
function deduction(k: string, raw: string | null | undefined, currency?: string): ContiRow {
  const v = euroStr(raw, currency);
  return v === "n/d" ? { k, v } : { k, v: `− ${v}`, cls: "text-red" };
}

/** D2 (Davide 2026-07-16): il breakdown si mostra anche quando `computable` è
 *  false. Il backend espone lordo/IVA/netto/commissioni/spedizione/imballo
 *  pure sugli ordini non calcolabili (`test_economics_service.py`: `commission
 *  == 18.30` con `computable=False`): sono numeri veri, prima li buttavamo via.
 *  Le 4 voci che dipendono dal costo prodotto arrivano `null` dal backend e
 *  degradano da sole a «n/d» via euroStr — nessun ramo da scrivere qui. */
function contiRows(row: OrderMarginRow, currency?: string): ContiRow[] {
  const marginKnown = row.margin != null;
  const marginNeg = marginKnown && Number(row.margin) < 0;
  return [
    { k: "Ricavo lordo", v: euroStr(row.gross_revenue, currency), strong: true },
    deduction("IVA", row.vat, currency),
    { k: "Netto", v: euroStr(row.net_revenue, currency) },
    deduction("Commissioni", row.commission, currency),
    deduction("Spedizione", row.shipping, currency),
    // Imballo già dentro il costo fornitore (riga «Bancale per…» fatturata): il
    // backend non l'ha sommato a total_cost, quindi qui non è una detrazione o
    // la colonna non quadrerebbe. La chiave resta la stringa esatta «Imballo».
    row.packaging_in_supplier_cost
      ? { k: "Imballo", v: `${euroStr(row.packaging, currency)} · già nel costo fornitore` }
      : deduction("Imballo", row.packaging, currency),
    deduction("Costo prodotto", row.product_cost, currency),
    { k: "Costo totale", v: euroStr(row.total_cost, currency) },
    {
      k: "Margine",
      v: marginKnown
        ? `${euroStr(row.margin, currency)} · ${percentStr(row.margin_percent)}`
        : "n/d",
      strong: true,
      // Colore per segno come MarginNetLine/ProductsSection: un ordine in perdita
      // (margine < 0) NON deve apparire in verde (colore guadagno) — bug-hunt
      // 2026-07-13. Margine ignoto → grigio: il verde è un giudizio che qui non
      // possiamo dare.
      cls: !marginKnown ? "text-ink" : marginNeg ? "text-red" : "text-green",
    },
  ];
}

/** Le tre che decidono: quanto entra, quanto esce, quanto resta. Le altre sei
 *  sono la loro scomposizione — vere, ma non si guardano ad ogni ordine. */
const IN_CHIARO = new Set(["Ricavo lordo", "Costo totale", "Margine"]);

function Riga({ r }: { r: ContiRow }) {
  return (
    <div className="flex justify-between gap-3 border-t border-line py-1.5 text-[13px] first:border-t-0">
      <span className={LABEL}>{r.k}</span>
      <span
        className={[
          "tabular-nums",
          r.strong ? "text-[14px] font-bold" : "font-semibold",
          r.cls ?? "",
        ].join(" ")}
      >
        {r.v}
      </span>
    </div>
  );
}

interface ContiSectionProps {
  order: Order;
  row: OrderMarginRow | null;
  syncedAt: string | null;
}

/** Conti order-level dai margini AM (stessa fonte della card: useOrderMargins).
 *  row null o componenti mancanti → degradazione onesta, mai numeri inventati. */
export function ContiSection({ order, row, syncedAt }: ContiSectionProps) {
  const rows = row ? contiRows(row, order.currency) : [];
  const principali = rows.filter((r) => IN_CHIARO.has(r.k) && r.k !== "Margine");
  // Il margine è la risposta: chiude la colonna «entra · esce · resta» come il
  // totale chiude uno scontrino, in grande. Stesso testo e stesso colore per
  // segno di prima — cambia solo la taglia.
  const margine = rows.find((r) => r.k === "Margine");
  const dettaglio = rows.filter((r) => !IN_CHIARO.has(r.k));
  // Un ordine in perdita non si nasconde dietro un triangolino: tono rosso, e
  // il rosso in `Collapsible` non e' richiudibile. Margine ignoto → ambra: c'e'
  // un dato che manca, e le reason che lo spiegano stanno lì dentro.
  const tono: CollapsibleTone =
    row && row.margin != null && Number(row.margin) < 0
      ? "red"
      : row && !row.computable
        ? "amber"
        : "neutral";
  // `is_estimate = bool(reasons)` lato backend: sul ramo stima la nota non è mai
  // vuota, ma su `computable=false` la parentesi vuota resterebbe possibile.
  const reasonsNote =
    row && row.estimate_reasons.length > 0 ? ` (${row.estimate_reasons.join(", ")})` : "";
  return (
    <section
      className={[
        PANEL,
        // Lo stato vince: un ordine in perdita non deve leggersi come «i soldi».
        tono === "neutral" ? AREA_CLS.soldi : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <h3 className={`mb-2 ${TITLE}`}>Conti</h3>
      {row ? (
        <>
          {principali.map((r) => (
            <Riga key={r.k} r={r} />
          ))}
          {margine && (
            <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-3 border-t-2 border-line-2 pt-2.5">
              <span className="text-[13px] font-bold text-ink">Margine</span>
              <span
                className={`text-[22px] font-bold leading-tight tabular-nums ${margine.cls ?? ""}`}
              >
                {margine.v}
              </span>
            </div>
          )}
          {dettaglio.length > 0 && (
            <Collapsible
              id="conti-dettaglio"
              title="Dettaglio"
              summary={`· ${dettaglio.length} voci`}
              tone={tono}
              // Niente colore d'area: è un pannello DENTRO i Conti, che il verde
              // ce l'hanno già. Due bordi verdi uno dentro l'altro erano rumore.
              annidato
              className="mt-3"
            >
              {dettaglio.map((r) => (
                <Riga key={r.k} r={r} />
              ))}
            </Collapsible>
          )}
          {/* Il perché del «n/d» sulle 4 voci sta nelle reason del backend
              (es. «costo prodotto parziale (righe senza costo)»). */}
          {!row.computable ? (
            <div className="mt-2 text-[12px] text-ink">Margine non calcolabile{reasonsNote}</div>
          ) : (
            row.is_estimate && <div className="mt-2 text-[12px] text-ink">≈ stima{reasonsNote}</div>
          )}
          {syncedAt && (
            <div className="mt-1.5 text-[11.5px] text-ink">
              {/* «ultimo cambio», non «aggiornato»: da quando l'upsert del
                  mirror timbra synced_at solo su variazione reale, questa data
                  dice quando un parametro e' CAMBIATO l'ultima volta — il sync
                  da AM gira lo stesso ogni giorno. */}
              Parametri AM · ultimo cambio {new Date(syncedAt).toLocaleDateString("it-IT")}
            </div>
          )}
        </>
      ) : (
        // Margini non caricati: non c'è NIENTE da scomporre, si ripiega sui due
        // numeri che l'ordine porta da sé. Diverso da `computable=false`, dove
        // il breakdown esiste ed è vero (vedi contiRows).
        <div className="text-[13px] text-ink">
          Margine non calcolabile per questo ordine.
          <div className="mt-1.5 flex justify-between border-t border-line pt-1.5">
            <span>Totale</span>
            <span className="font-medium tabular-nums">
              {formatMoney(order.total, order.currency)}
            </span>
          </div>
          {order.costo > 0 && (
            <div className="flex justify-between border-t border-line py-1.5">
              <span>Costo fornitore</span>
              <span className="font-medium tabular-nums">
                {formatMoney(order.costo, order.currency)}
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
